import type { ComponentType, ReactNode } from 'react';
import { AlertTriangleIcon, CheckCircle2Icon, InboxIcon } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { describeError } from '@/lib/error-text';

/**
 * Xato — `role="alert"` bilan.
 *
 * Serverdan kelgan xato uchun jumlani `describeError` beradi: API hech qachon foydalanuvchi
 * ko'radigan gap yubormaydi, ya'ni kod → jumla tarjimasi bitta joyda turishi kerak. Formaning
 * o'z xatosi (server umuman chaqirilmagan hol) esa `children` orqali beriladi.
 */
export function ErrorNotice({
  error,
  children,
  className,
}: {
  error?: unknown;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <Alert variant="destructive" role="alert" className={cn('motion-rise', className)}>
      <AlertTriangleIcon aria-hidden="true" />
      <AlertDescription>{children ?? describeError(error)}</AlertDescription>
    </Alert>
  );
}

/** Serverdan kelmagan, ekranning o'zi aytadigan ogohlantirish (masalan "navbatga ulanib bo'lmadi"). */
export function WarningNotice({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <Alert variant="warning" role="alert" className={cn('motion-rise', className)}>
      <AlertTriangleIcon aria-hidden="true" />
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}

/** Muvaffaqiyatli amal tasdiqlanishi — `role="status"`, ya'ni ekran o'quvchisi uni jimgina o'qiydi. */
export function SuccessNotice({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <Alert variant="success" role="status" className={cn('motion-rise', className)}>
      <CheckCircle2Icon aria-hidden="true" />
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}

export interface EmptyStateProps {
  title: ReactNode;
  description?: ReactNode;
  icon?: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  className?: string;
}

export function EmptyState({
  title,
  description,
  icon: Icon = InboxIcon,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'border-border/70 text-muted-foreground motion-fade flex flex-col items-center gap-3 rounded-lg border border-dashed px-6 py-10 text-center',
        className,
      )}
    >
      {/*
        Belgi doira ichida: bo'sh ekranda u yagona ko'rinadigan narsa, va chegarasiz u
        sahifada tasodifan qolib ketgan piktogrammaday ko'rinardi.
      */}
      <span
        aria-hidden="true"
        className="bg-muted/60 text-muted-foreground/70 flex size-11 items-center justify-center rounded-full"
      >
        <Icon className="size-5" aria-hidden />
      </span>
      <p className="text-foreground text-sm font-medium">{title}</p>
      {description ? <p className="max-w-prose text-xs">{description}</p> : null}
    </div>
  );
}

/** Jadval yuklanayotgandagi skelet — ustunlar soni bo'yicha, ya'ni yuklangach layout sakramaydi. */
export function TableSkeleton({ columns, rows = 3 }: { columns: number; rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, row) => (
        <TableRow key={row} aria-hidden="true">
          {Array.from({ length: columns }, (_, column) => (
            <TableCell key={column}>
              <Skeleton className="h-4 w-full max-w-28" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}
